import {
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  IconButton,
  Popper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { tokens } from '../../global/theme/tokens';
import { API_DELETE, API_PATCH, DBRequest, useConfig } from '../../api/api';
import { ProjectAddElementButton } from './project/ProjectAddElementButton';
import ExoDialog from './ExoDialog';
import { NewTask } from '../new/NewTask';
import { ExoSkeleton } from './ExoSkeleton';
import DateLabel from '../label/DateLabel';
import { TimeLabel } from '../label/TimeLabel';
import EditIcon from '@mui/icons-material/Edit';
import CallIcon from '@mui/icons-material/Call';
import EmailIcon from '@mui/icons-material/Email';
import { updateOrCreate } from '../special/updateOrCreate';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RenderHTML from '../preview/RenderHTML';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import DeleteIcon from '@mui/icons-material/Delete';
import ExoAlert from './ExoAlert';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { checkDeadlinePassed } from '../special/checkDeadlinePassed';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';

export const ExoTasks = ({
  tasks,
  toggelSx,
  noNew,
  targetType,
  targetId,
  onEdit,
  onChange,
  onStatusChange,
  onFeedback,
  isLoading,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();

  const [showStatus, setShowStatus] = useState(0);
  const [openNewTask, setOpenNewTask] = useState(false);
  const [reqDel, setReqDel] = useState(null);

  //for debounce
  const latestTasksRef = useRef(tasks);
  const latestShowStatusRef = useRef(showStatus);
  const removalTimeoutsRef = useRef({});

  // Sync the latest task list with a ref
  useEffect(() => {
    latestTasksRef.current = tasks;
  }, [tasks]);

  // Sync the current status filter (0 = Pending, 1 = Completed) with a ref
  useEffect(() => {
    latestShowStatusRef.current = showStatus;
  }, [showStatus]);

  function handleDelete(element) {
    setReqDel(null);
    DBRequest({
      config,
      path: `user-tasks/${element.id}`,
      method: API_DELETE,
      onResponse: () => {
        const newTasks = tasks.data.filter((obj) => obj.id !== element.id);
        onChange({ ...tasks, data: newTasks });
      },
      onFeedback,
    });
  }
  //Update the tasks after 5 seconds
  function updateTasksWithDebounce(res) {
    const updatedTask = res.data;
    const taskId = updatedTask.id;

    // Immediately update the UI with the new task status
    const newTasks = updateOrCreate(tasks, updatedTask);
    latestTasksRef.current = newTasks;
    onChange(newTasks);

    // Cancel existing timeout for this task (if any)
    const existingTimeout = removalTimeoutsRef.current[taskId];
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      delete removalTimeoutsRef.current[taskId];
    }

    // If status no longer matches current filter, schedule removal
    if (updatedTask.status !== latestShowStatusRef.current) {
      const timeoutId = setTimeout(() => {
        const latestTasks = latestTasksRef.current;
        const latestStatus = latestShowStatusRef.current;

        if (!latestTasks || latestStatus === null || latestStatus === undefined)
          return;

        const stillMismatched =
          latestTasks.data.find((t) => t.id === taskId)?.status !==
          latestStatus;

        if (stillMismatched) {
          const filtered = latestTasks.data.filter((t) => t.id !== taskId);
          onChange({ ...latestTasks, data: filtered });
        }

        delete removalTimeoutsRef.current[taskId];
      }, 5000);

      removalTimeoutsRef.current[taskId] = timeoutId;
    }
  }

  // task change
  function updateTasks(res) {
    const updatedTask = res.data;

    // Remove the task if its new status doesn't match the current filter
    if (updatedTask.status !== showStatus) {
      const filteredTasks = tasks.data.filter((t) => t.id !== updatedTask.id);

      onChange({ ...tasks, data: filteredTasks });
      return;
    }

    const newTasks = updateOrCreate(tasks, res.data);
    onChange(newTasks);
  }

  // Handle Add task from Dashboard
  function handleOpenTask() {
    setOpenNewTask(true);
  }

  if (isLoading)
    return (
      <Box className="w-full flex flex-col gap-2">
        <ExoSkeleton n={8} size="53px" />
      </Box>
    );

  return (
    <Box>
      <ToggleButtonGroup
        sx={{
          ...toggelSx,
          '& button': {
            padding: '3px 8px',
          },
        }}
        size="small"
        value={showStatus}
        exclusive
        onChange={(e, value) => {
          if (value !== showStatus && value !== null) {
            setShowStatus(value);
            onStatusChange(value);
          }
        }}
        aria-label="Task Status"
      >
        <ToggleButton value={0}>
          <PanoramaFishEyeIcon />
        </ToggleButton>
        <ToggleButton value={1}>
          <TaskAltIcon />
        </ToggleButton>
      </ToggleButtonGroup>

      <Box className="flex flex-col gap-1 pb-2">
        <ProjectAddElementButton
          sizePx="20px"
          size="small"
          addLabel={t('Add Task')}
          onClick={handleOpenTask}
          sx={{
            // border: '1px solid rgba(0, 0, 0, 0.12)', //for light mode
            border: '1px solid rgba(255, 255, 255, 0.12)',
          }}
        />
      </Box>
      <Box className="flex flex-col gap-1">
        {tasks ? (
          tasks?.data.map((element) => (
            <Task
              key={element.id}
              data={element}
              onEdit={() => onEdit(element)}
              onChange={updateTasksWithDebounce}
              onDelete={() => setReqDel(element)}
            />
          ))
        ) : (
          <ExoSkeleton n={6} />
        )}
      </Box>
      <ExoAlert
        onSubmit={() => handleDelete(reqDel)}
        open={Boolean(reqDel)}
        onClose={() => setReqDel(null)}
        alert={{
          submitLabel: t('Delete'),
          message: t('Are you sure you want to permanently delete this entry?'),
        }}
      />
      <ExoDialog
        open={openNewTask}
        onClose={() => setOpenNewTask(false)}
        limitWidth
      >
        <NewTask
          onClose={() => setOpenNewTask(false)}
          targetType={targetType}
          targetId={targetId}
          onCreate={updateTasks}
          onChange={updateTasks}
        />
      </ExoDialog>
    </Box>
  );
};

const Task = ({ data, onEdit, onChange, onDelete }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [openDiscription, setOpenDiscription] = useState(false);

  const { name, deadline, description, reminders, createdAt } = data;

  // Reminder Passed Tasks
  const deadlinePassed = deadline && checkDeadlinePassed(deadline);

  function handleStatusChange() {
    DBRequest({
      config,
      path: `user-tasks/${data.id}`,
      method: API_PATCH({ status: data.status ? 0 : 1 }),
      onResponse: onChange,
      onLoading: setIsLoading,
    });
  }

  const Icon = data.status ? TaskAltIcon : PanoramaFishEyeIcon;
  // call
  const [reminderAnchorEl, setreminderAnchorEl] = useState(null);
  function handleOpenNewCall(e) {
    setreminderAnchorEl(e.currentTarget);
  }

  function handleShowLead() {
    navigate(
      `leads/${data.targetType == 'companyLead' ? 'companies' : 'persons'}/${
        data.target.id
      }`
    );
  }

  return (
    <Box
      className="relative"
      sx={{
        bgcolor: colors.card,
        borderRadius: '10px',
        padding: '6px 8px',
        paddingBottom: deadline ? '20px' : '',
        '& .delButton': {
          transition: '0.3s ease',
          bgcolor: colors.error + '30',
          color: colors.error,
          border: `1px solid ${colors.error}80`,
          opacity: 0,
          transform: 'translate(-5px)',
          zIndex: 10,
        },
        '& .editButton': {
          transition: '0.3s ease',
          bgcolor: colors.success + '30',
          color: colors.success,
          border: `1px solid ${colors.success}80`,
          opacity: 0,
          transform: 'translate(-5px)',
          zIndex: 10,
        },
        ':hover': {
          '& .editButton, & .delButton': {
            opacity: 1,
            transform: 'translate(0px)',
          },
        },
      }}
    >
      <Box className="flex flex-row gap-2">
        {isLoading ? (
          <span
            className="loader"
            style={{
              width: '25px',
              height: '25px',
            }}
          ></span>
        ) : (
          <Button
            onClick={handleStatusChange}
            sx={{
              padding: '0px!important',
              width: '25px',
              height: '25px',
              minWidth: 'unset',
              borderRadius: '9999px',
            }}
          >
            <Icon sx={{ fontSize: '20px' }} />
          </Button>
        )}
        <Box className="flex flex-col w-full">
          <Box className="flex items-center justify-between gap-2">
            <Box className="flex gap-1 items-center w-full justify-between">
              <Typography fontSize="14px" textAlign="left" fontWeight={500}>
                {data.type === 'call' ? (
                  <CallIcon
                    sx={{ fontSize: '16px' }}
                    className="flex items-center justify-center"
                  />
                ) : data.type === 'email' ? (
                  <EmailIcon
                    sx={{ fontSize: '16px' }}
                    className="flex items-center justify-center"
                  />
                ) : data.type === 'other' ? (
                  <EditNoteIcon sx={{ fontSize: '20px' }} />
                ) : (
                  data.type === null
                )}{' '}
                {name}
              </Typography>

              <Box className="flex gap-1">
                <IconButton
                  onClick={onEdit}
                  size="small"
                  className="editButton"
                >
                  <EditIcon sx={{ fontSize: '15px' }} />
                </IconButton>
                <IconButton
                  onClick={onDelete}
                  size="small"
                  className="delButton"
                >
                  <DeleteIcon sx={{ fontSize: '15px' }} />
                </IconButton>
              </Box>
            </Box>
            {reminders?.length > 0 && (
              <Button
                sx={{
                  padding: '0px!important',
                  minWidth: '25px',
                  minHeight: '25px',
                  borderRadius: '9999px',
                  bgcolor: colors.warning + '30',
                }}
                className="flex items-center justify-center"
                onClick={handleOpenNewCall}
              >
                <AccessAlarmIcon
                  sx={{ fontSize: '16px', color: colors.warning }}
                />
              </Button>
            )}
          </Box>
          {/* popper to see reminder details  */}
          <Popper
            open={Boolean(reminderAnchorEl)}
            anchorEl={reminderAnchorEl}
            placement="bottom-end"
          >
            <ClickAwayListener onClickAway={() => setreminderAnchorEl(null)}>
              <Box
                className="px-4 py-2 rounded-lg flex flex-col gap-1 backdrop-blur-md"
                sx={{
                  background: `linear-gradient(135deg,  ${colors.glassFrom}, ${colors.glassTo})`,
                  border: `1px solid ${colors.selected}`,
                }}
              >
                {/* Conditionally rendering the correct icon */}
                <Typography fontWeight={500}>
                  {data.type === 'call' ? (
                    <CallIcon sx={{ fontSize: '15px' }} />
                  ) : data.type === 'email' ? (
                    <EmailIcon sx={{ fontSize: '15px' }} />
                  ) : data.type === 'other' ? (
                    <EditNoteIcon sx={{ fontSize: '15px' }} />
                  ) : (
                    data.type === null
                  )}
                  {`${t(data.type)}`}
                </Typography>

                {/* Reminders - Displaying each reminder */}
                {data.reminders && data.reminders.length > 0 && (
                  <Box>
                    <Typography
                      fontWeight={500}
                      className="opacity-50"
                      fontSize={11}
                    >
                      {t('Reminders')}:
                    </Typography>
                    {data.reminders.map((reminder) => (
                      <Box key={reminder.id} sx={{ marginBottom: 1 }}>
                        <Typography fontWeight={500}>
                          <AccessAlarmIcon
                            sx={{ fontSize: '16px', color: colors.warning }}
                          />
                          {t(' : ')}
                          <DateLabel date={reminder.remindAt} /> -{' '}
                          <TimeLabel time={reminder.remindAt} format="HH:mm" />
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </ClickAwayListener>
          </Popper>

          {deadline && (
            <Box
              className="flex items-center gap-1 mt-1"
              sx={{ color: deadlinePassed ? colors.error : 'inherit' }}
            >
              <HourglassBottomIcon
                sx={{
                  fontSize: '16px',
                }}
              />
              <Typography fontSize="12px" fontWeight={500}>
                <DateLabel date={deadline} />
                {' - '}
                <TimeLabel time={deadline} format="HH:mm"></TimeLabel>
              </Typography>
            </Box>
          )}

          {(description || data?.target) && (
            <Box className="flex flex-col gap-1">
              <Button
                sx={{ padding: 0, justifyContent: 'space-between' }}
                onClick={() => setOpenDiscription(!openDiscription)}
                className="flex items-center justify-between"
              >
                <Typography
                  fontWeight={500}
                  fontSize="12px"
                  className="opacity-60"
                >
                  {openDiscription ? t('show less...') : t('show more...')}
                </Typography>
                <ArrowDropDownIcon
                  sx={{
                    transform: `rotate(${openDiscription ? 180 : 0}deg)`,
                  }}
                />
              </Button>
              <Collapse in={openDiscription}>
                <Box className="flex flex-col gap-1">
                  {data?.target && (
                    <Box className="flex gap-2 items-center">
                      {data?.target?.logo && (
                        <img
                          className="w-[32px] h-[32px] object-contain"
                          src={data.target.logo.temporaryUrl}
                        />
                      )}
                      <Typography
                        onClick={() => handleShowLead()}
                        fontWeight={600}
                        sx={{ cursor: 'pointer' }}
                      >
                        {data.target.name}
                      </Typography>
                    </Box>
                  )}
                  <RenderHTML HTML={description} emptyHTML={' '} />
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>
      </Box>

      {createdAt && (
        <Typography
          fontSize="11px"
          className="opacity-50 absolute bottom-1 right-2"
        >
          <DateLabel date={createdAt} />
        </Typography>
      )}
    </Box>
  );
};
